import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { Router } from '@angular/router';
import { ItemIconComponent } from '../icon/item-icon.component';
import { SUBICONS_ALL } from '../icon/subicons/item-subicons.component';
import { ItemGridLayoutComponent } from '../grid/item-grid-layout.component';
import { SectionQuickActionsComponent } from '@app/components/shared/quick-actions/section-quick-actions.component';
import { DataService } from '@app/services/data.service';
import { StorageService } from '@app/services/storage.service';
import { ItemHelper } from '@app/helpers/item-helper';
import { Maybe } from '@app/types/maybe';
import { IItem, ItemType } from 'skygame-data';
import { nanoid } from 'nanoid';
import { TooltipDirective } from '@app/directives/tooltip.directive';

interface IItemCollection {
  guid: string;
  name: string;
  description: string;
  imageUrl: string;
  items: Array<IItem>;
}

interface IStorageData {
  collections: Array<{ guid: string, name: string, description: string, imageUrl?: string, items: string }>;
}

@Component({
  selector: 'app-item-collection',
  templateUrl: './item-collection.component.html',
  styleUrl: './item-collection.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TooltipDirective, RouterLink, MatIcon, ReactiveFormsModule, ItemIconComponent, ItemGridLayoutComponent, SectionQuickActionsComponent]
})
export class ItemCollectionComponent {
  readonly SUBICONS_ALL = SUBICONS_ALL;
  readonly allItems: ReadonlyArray<IItem>;
  readonly initialCategory = ItemType.Outfit;

  showEdit = false;
  editImgSrc?: string;
  editImgError?: boolean;
  editItems: Array<IItem> = [];
  editItemSet = new Set<IItem>();

  editGuid?: string;

  collections: Array<IItemCollection> = [];

  readonly editForm = new FormGroup({
    name: new FormControl('', { nonNullable: true }),
    description: new FormControl('', { nonNullable: true }),
    imageUrl: new FormControl('', { nonNullable: true }),
  });

  @HostListener('window:beforeunload', ['$event'])
  beforeUnloadHandler(event: Event): void {
    if (this.editItems.length > 0) { event.preventDefault(); }
  }

  constructor(
    private readonly _storageService: StorageService,
    private readonly _dataService: DataService,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _router: Router
  ) {
    this.allItems = this._dataService.itemConfig.items;
    this.editForm.controls.imageUrl.valueChanges.subscribe(v => this.updateImgAdd(v ?? ''));
    this.loadStorage();
    this.readItemsFromUrl();
  }

  onItemClickedPicker(item: IItem): void {
    this.editItemSet.has(item) ? this.removeItem(item) : this.addItem(item);
  }

  toggleShowAdd(): void {
    if (this.showEdit && this.editGuid) {
      if (!confirm(`You're currently editing a collection. Are you sure you want to reset and add a new collection?`)) { return; }
      this.clearEditForm();
      this.editGuid = undefined;
      return;
    } else if (this.editItems.length) {
      if (!confirm(`You have items selected. Are you sure you want to stop adding a new collection?`)) { return; }
      this.clearEditForm();
    }

    this.clearEditForm();
    this.showEdit = !this.showEdit;
    this.editGuid = undefined;
  }

  addCollectionFromBottom(): void {
    const wasEditing = this.showEdit;
    this.toggleShowAdd();
    if (this.showEdit && !wasEditing) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }

  importCollectionFile(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';
    input.onchange = async (event: Event) => {
      const target = event.target as HTMLInputElement;
      if (!target.files || !target.files.length) { return; }
      const file = target.files[0];
      const json = await file.text();
      try {
        const collections: Array<IItemCollection> = this.deserializeCollections(json);
        if (!collections.length) { throw new Error('No collections found in file.'); }
        if (!confirm(`You're trying to import ${collections.length} new collection${collections.length === 1 ? '' : 's'}. Continue?`)) { return; }
        collections.forEach(c => {
          c.guid = nanoid(10);
          this.collections.push(c);
        });

        this.saveStorage();
        this._changeDetectorRef.markForCheck();
      } catch {
        alert('Failed to import collection. Please ensure the file is a valid collection.');
      }
    };
    input.click();
  }

  addItem(item: IItem): void {
    this.editItemSet.add(item);
    this.editItems = Array.from(this.editItemSet);
  }

  removeItem(item: IItem): void {
    this.editItemSet.delete(item);
    this.editItems = Array.from(this.editItemSet);
  }

  updateImgAdd(value: string): void {
    const regexHttp = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/g;
    this.editImgSrc = regexHttp.test(value) ? value : '';
    this.editImgError = !this.editImgSrc && value.length > 0;
  }

  moveCollectionUp(i: number, evt?: Event): void {
    evt?.preventDefault();
    evt?.stopImmediatePropagation();
    if (i <= 0) { return; }
    const collection = this.collections[i];
    this.collections[i] = this.collections[i - 1];
    this.collections[i - 1] = collection;
    this.saveStorage();
  }

  moveCollectionDown(i: number, evt?: Event): void {
    evt?.preventDefault();
    evt?.stopImmediatePropagation();
    if (i >= this.collections.length - 1) { return; }
    const collection = this.collections[i];
    this.collections[i] = this.collections[i + 1];
    this.collections[i + 1] = collection;
    this.saveStorage();
  }

  saveCollection(): void {
    const formValue = this.editForm.getRawValue();
    const collection: IItemCollection = {
      guid: this.editGuid || nanoid(10),
      name: formValue.name,
      description: formValue.description,
      imageUrl: this.editImgSrc || '',
      items: this.editItems
    };

    if (this.editGuid) {
      const index = this.collections.findIndex(c => c.guid === this.editGuid);
      if (index < 0) {
        alert(`Collection was not found. Please refresh the page. Sorry.`);
        return;
      }
      this.collections[index] = collection;
    } else {
      this.collections.push(collection);
    }

    const isNew = !this.editGuid;
    this.saveStorage();
    this.editGuid = undefined;
    this.showEdit = false;
    this.clearEditForm();

    // Wait for the list to render again before it can be scrolled to.
    setTimeout(() => {
      if (isNew) {
        window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'instant' });
      } else {
        const el = document.querySelector(`[data-collection="${collection.guid}"]`);
        el?.scrollIntoView({ behavior: 'instant', block: 'nearest' });
      }
    });
  }

  cancelEdit(): void {
    if (!confirm('Are you sure you want to cancel editing this collection?')) { return; }
    this.editGuid = undefined;
    this.showEdit = false;
    this.clearEditForm();
  }

  gotoCalculator(collection: IItemCollection, evt?: Event): void {
    evt?.preventDefault();
    evt?.stopImmediatePropagation();
    const ids = ItemHelper.serializeQuery(collection.items);
    void this._router.navigate(['/item/unlock-calculator'], { queryParams: { items: ids } });
  }

  showEditCollection(collection: IItemCollection, evt?: Event): void {
    this.updateEditForm(collection, evt);
    this.editGuid = collection.guid;
    this.showEdit = true;
  }

  showCopyCollection(collection: IItemCollection, evt?: Event): void {
    this.updateEditForm(collection, evt);
    this.editGuid = undefined;
    this.showEdit = true;
  }

  clearEditForm(): void {
    this.editItems = [];
    this.editItemSet.clear();
    this.editForm.reset({ name: '', description: '', imageUrl: '' });
  }

  updateEditForm(collection: IItemCollection, evt?: Event): void {
    evt?.preventDefault();
    evt?.stopImmediatePropagation();
    if (this.editItems.length && !confirm('Copying this collection will overwrite your current selection to create a new collection. Continue?')) { return; }
    this.editItems = [...collection.items];
    this.editItemSet = new Set(this.editItems);
    this.editForm.setValue({
      name: collection.name || '',
      description: collection.description || '',
      imageUrl: collection.imageUrl || ''
    });
  }

  exportCollection(collection: IItemCollection, evt?: Event): void {
    evt?.preventDefault();
    evt?.stopImmediatePropagation();
    const data = this.serializeCollections([collection]);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sky-planner-collection.json';
    a.click();
    URL.revokeObjectURL(url);
  }

  promptDeleteCollection(collection: IItemCollection, evt?: Event): void {
    evt?.preventDefault();
    evt?.stopImmediatePropagation();
    if (!confirm(`Are you sure you want to delete the collection "${collection.name}"?`)) { return; }
    this.collections = this.collections.filter(c => c !== collection);
    this.saveStorage();
  }

  collectionUnlockedCount(collection: IItemCollection): number {
    return collection.items.filter(i => i.unlocked).length;
  }

  private readItemsFromUrl(): void {
    const url = new URL(location.href);
    const sIds = url.searchParams.get('items');
    if (!sIds) { return; }

    this.editItems = [];
    this.editItemSet.clear();
    this.editGuid = undefined;
    this.showEdit = true;
    const ids = ItemHelper.deserializeQuery(sIds);
    for (const id of ids) {
      const item = this._dataService.itemIdMap.get(id);
      if (item) { this.addItem(item); }
    }
  }

  private loadStorage(): void {
    const data: Maybe<IStorageData> = this._storageService.getKey('item.collections');
    if (data) {
      this.collections = (data.collections || []).map(c => ({
        guid: c.guid, name: c.name, description: c.description, imageUrl: c.imageUrl || '',
        items: c.items?.split(',').map(guid => this._dataService.guidMap.get(guid) as IItem).filter(i => i) || []
      }));
    }
  }

  private saveStorage(): void {
    this._storageService.setKey('item.collections', {
      collections: this.collections.map(c => ({
        guid: c.guid, name: c.name, description: c.description, imageUrl: c.imageUrl,
        items: c.items.map(i => i.guid).join(',')
      }))
    });
  }

  private serializeCollections(collections: Array<IItemCollection>): string {
    return JSON.stringify(collections.map(c => ({
      guid: c.guid,
      name: c.name,
      description: c.description,
      imageUrl: c.imageUrl,
      items: c.items.map(i => i.guid)
    })), null, 2);
  }

  private deserializeCollections(json: string): Array<IItemCollection> {
    const collections = JSON.parse(json);
    if (!Array.isArray(collections)) { throw new Error('Invalid collection data.'); }
    return collections.map(c => ({
      guid: c.guid,
      name: c.name,
      description: c.description,
      imageUrl: c.imageUrl,
      items: c.items.map((guid: string) => this._dataService.guidMap.get(guid) as IItem).filter((i: IItem) => i)
    }));
  }
}
